import { useState } from "react";
import AdminHeader from "../../component/AdminHeader";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomSelect from "../../component/CustomSelect";
import AdditionalSchemas, { validateOptionalSchemas } from "./AdditionalSchemas";
import {
  buildPageSeoPayload,
  ogLocaleOptions,
  ogTypeOptions,
  synchronizeSeoFields,
  updateSeoField,
} from "./SeoFieldOptions";

const seoCreateEndpoint = ApiEndpoints.seo.seoCreate;

export default function SeoCreate() {
  const [seoData, setSeoData] = useState(synchronizeSeoFields({
    url: "",
    title: "",
    description: "",
    keywords: "",
    canonical_tag: "",
    faq_schema: "",
    og_locale: "en_US",
    og_type: "website",
    og_title: "",
    og_description: "",
    og_url: "",
    og_image: "",
    twitter_title: "",
    twitter_description: "",
    twitter_image: "",
    article_schema: "",
    additional_schemas: [],
  }));
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSeoData((prevData) => updateSeoField(prevData, name, value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const key of ["url", "title", "description", "keywords"]) {
      if (!seoData[key] || seoData[key].trim() === "") {
        toast.error(`The field "${key}" is mandatory.`);
        return;
      }
    }

    try {
      validateOptionalSchemas(seoData);
    } catch (error) {
      toast.error("Please enter valid JSON in every non-empty schema field.");
      return;
    }

    try {
      const response = await ApiRequest("POST", seoCreateEndpoint, buildPageSeoPayload(seoData));
      if (response.error === false) {
        toast.success(response.message);
        navigate("/seo");
      } else {
        toast.error(response.message || "Failed to create SEO.");
      }
    } catch (error) {
      toast.error(error.response?.data?.errors?.[0] || error.response?.data?.message || "Error creating SEO.");
    }
  };

  const pagename = {
    name: "SEO Management",
    user: "Admin",
    innerpage: "SEO Management / Add SEO",
  };

  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        <form id="seoAdd" onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>URL<small>*</small></label></div>
                  <input type="text" name="url" value={seoData.url} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Meta Title<small>*</small></label></div>
                  <input type="text" name="title" value={seoData.title} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Meta Description<small>*</small></label></div>
                  <input type="text" name="description" value={seoData.description} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Meta Keywords<small>*</small></label></div>
                  <input type="text" name="keywords" value={seoData.keywords} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Canonical Tag</label></div>
                  <input type="text" name="canonical_tag" value={seoData.canonical_tag} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>OG Locale</label></div>
                  <CustomSelect
                    name="og_locale"
                    options={ogLocaleOptions.map(([value, label]) => ({ _id: value, name: `${label} (${value})` }))}
                    defaultValue={seoData.og_locale}
                    placeholder="Select OG Locale"
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>OG Type</label></div>
                  <CustomSelect
                    name="og_type"
                    options={ogTypeOptions.map((type) => ({ _id: type, name: type }))}
                    defaultValue={seoData.og_type}
                    placeholder="Select OG Type"
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>OG Image</label></div>
                  <input type="text" name="og_image" value={seoData.og_image} onChange={handleChange} className="form-control" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>OG Title</label></div>
                  <input type="text" name="og_title" value={seoData.og_title} className="form-control" disabled />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>OG Description</label></div>
                  <input type="text" name="og_description" value={seoData.og_description} className="form-control" disabled />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>OG URL</label></div>
                  <input type="text" name="og_url" value={seoData.og_url} className="form-control" disabled />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Twitter Title</label></div>
                  <input type="text" name="twitter_title" value={seoData.twitter_title} className="form-control" disabled />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Twitter Description</label></div>
                  <input type="text" name="twitter_description" value={seoData.twitter_description} className="form-control" disabled />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Twitter Image</label></div>
                  <input type="text" name="twitter_image" value={seoData.twitter_image} className="form-control" disabled />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>FAQ Schema</label></div>
                  <textarea name="faq_schema" value={seoData.faq_schema} onChange={handleChange} className="form-control" rows="5" style={{ height: "auto" }} />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Article Schema</label></div>
                  <textarea name="article_schema" value={seoData.article_schema} onChange={handleChange} className="form-control" rows="5" style={{ height: "auto" }} />
                </div>
              </div>
            </div>
          </div>

          <AdditionalSchemas
            value={seoData.additional_schemas}
            onChange={(additional_schemas) =>
              setSeoData((current) => ({ ...current, additional_schemas }))
            }
          />
        </form>

        <button type="submit" form="seoAdd" className="default-btn">
          Publish <span></span>
        </button>
      </div>
    </>
  );
}
